// TEMP band-aid for getFormattedName — a hardcoded seriesId → display map used
// ONLY when VAPI isn't passed to the formatter. getFormattedName is synchronous
// and called in ~20 places that have the seriesId but not the VAPI `seriesName`
// (or nav `title`); the legacy switch below only matches the spaced/hyphenated
// forms (e.g. "gr corolla"), so a canonical id like "grcorolla" would otherwise
// render "Grcorolla". (Production display path — not test-only.)
//
// This duplicates a real source of truth (VAPI `seriesName`; nav `title` has it
// too) and can drift — the same smell as the deleted registry.
//
// TODO: delete this map once all ~20 getFormattedName callers pass the VAPI
// `seriesName` / nav `title` directly ("Full display consolidation → VAPI
// seriesName" — see .github/plans/vehicle-identity/ §7 Planned TODOs).
const CANONICAL_DISPLAY = {
  priusprime: "Prius Prime",
  priuspluginhybrid: "Prius Plug-in Hybrid",
  rav4prime: "RAV4 Prime",
  rav4pluginhybrid: "RAV4 Plug-in Hybrid",
  grcorolla: "GR Corolla",
  grsupra: "GR Supra",
  corollacross: "Corolla Cross",
  corollahatchback: "Corolla Hatchback",
  corollaim: "Corolla iM",
  grandhighlander: "Grand Highlander",
  landcruiser: "Land Cruiser",
  toyotacrown: "Toyota Crown",
  toyotacrownsignia: "Toyota Crown Signia",
  bzwoodland: "bZ Woodland",
  yarishatchback: "Yaris Hatchback",
  yarissedan: "Yaris Sedan",
  yarisia: "Yaris iA",
  priusc: "Prius *c*",
  priusv: "Prius *v*",
};

export function getFormattedName(modelName, uppercase = false, modelYear = -1) {
  modelName = modelName.toLowerCase();
  const canonical = CANONICAL_DISPLAY[modelName];
  if (canonical) {
    return uppercase && !canonical.toLowerCase().includes("bz")
      ? canonical.toUpperCase()
      : canonical;
  }
  switch (modelName) {
    case "bz4x":
      modelName = "bZ4X";
      break;
    case "bz":
      modelName = "bZ";
      break;
    case "bz woodland":
      modelName = "bZ Woodland";
      break;
    case "c-hr":
    case "chr":
    case "**chr**":
      modelName = "C-HR";
      break;
    case "**c-hr**":
      modelName = "**C-HR**";
      break;
    case "prius c":
      modelName = "Prius *c*";
      break;
    case "**prius c**":
      modelName = "**Prius *c***";
      break;
    case "land cruiser":
      modelName = "Land Cruiser";
      break;
    case "**land cruiser**":
      modelName = "**Land Cruiser**";
      break;
    case "prime":
      modelName = "Prius Prime";
      break;
    case "**prime**":
    case "**prius prime**":
      modelName = "**Prius Prime**";
      break;
    case "prius plugin hybrid":
    case "prius plug-in hybrid":
    case "plugin":
    case "plug-in":
    case "prius prime":
      if (modelYear >= 2025) {
        modelName = "Prius Plug-in Hybrid";
      } else {
        modelName = "Prius Prime";
      }
      break;
    case "4runner":
      modelName = "4Runner";
      break;
    case "**4runner**":
      modelName = "**4Runner**";
      break;
    case "rav4":
      modelName = "RAV4";
      break;
    case "**rav4**":
      modelName = "**RAV4**";
      break;
    case "rav4 prime":
    case "rav4 plug-in hybrid":
      if (modelYear >= 2025) {
        modelName = "RAV4 Plug-in Hybrid";
      } else {
        modelName = "RAV4 Prime";
      }
      break;
    case "corolla cross":
    case "Corolla cross":
      modelName = "Corolla Cross";
      break;
    case "**corolla cross**":
      modelName = "**Corolla Cross**";
      break;
    case "corolla hatchback":
      modelName = "Corolla Hatchback";
      break;
    case "**corolla hatchback**":
      modelName = "**Corolla Hatchback**";
      break;
    case "yaris sedan":
      modelName = "Yaris Sedan";
      break;
    case "**yaris sedan**":
      modelName = "**Yaris Sedan**";
      break;
    case "gr supra":
    case "supra":
      modelName = "GR Supra";
      break;
    case "gr corolla":
      modelName = "GR Corolla";
      break;
    case "86":
    case "gr86":
      modelName = "GR86";
      break;
    case "toyota crown":
      modelName = "Toyota Crown";
      break;
    case "toyota crown signia":
      modelName = "Toyota Crown Signia";
      break;
    case "grand highlander":
      modelName = "Grand Highlander";
      break;
    default:
      modelName = modelName
        ? modelName.charAt(0).toUpperCase() + modelName.slice(1)
        : "";
  }
  if (uppercase) {
    if (!modelName.toLowerCase().includes("bz")) {
      modelName = modelName.toUpperCase();
    }
  }
  return modelName;
}

export function getFormattedTrim(trim) {
  switch (trim.toLowerCase()) {
    case "l eco":
      trim = "L *Eco*";
      break;
    case "l eco (fwd 1.8l 4-cyl. hybrid)":
      trim = "L *Eco* (FWD 1.8L 4-Cyl. Hybrid)";
      break;
    case "awd-e":
      trim = "AWD-*e*";
      break;
    case "le awd-e":
      trim = "LE AWD-*e*";
      break;
    case "le (awd-e 1.8l 4-cyl. hybrid)":
      trim = "LE (AWD-*e* 1.8L 4-Cyl. Hybrid) ";
      break;
    case "xle (awd-e 1.8l 4-cyl. hybrid)":
      trim = "XLE (AWD-*e* 1.8L 4-Cyl. Hybrid) ";
      break;
    default:
      break;
  }
  return trim;
}

export function formatModelListItemName(listName, trimName) {
  const formattedName = listName
    .replace(trimName, "")
    .replace("(", "")
    .replace(")", "");

  if (formattedName === "") {
    return trimName;
  } else {
    return formattedName;
  }
}

export function getModelNameForSearchResult(modelName) {
  if (modelName) {
    switch (modelName.toLowerCase()) {
      case "c-hr chr":
        modelName = "C-hr"; // This is a use case for chr index search.
        break;
      default:
        break;
    }
  }
  return modelName;
}
